import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { addErrorNotification, loadGiftCardDetails } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { forwardRef, useReducer, useRef } from 'react';
import { useOccConfig } from '../../../context/occ/hooks/use-occ-config';
import { useFormValidator } from '../../../hooks/use-form-validator';
import { Form } from '../../../subcomponents/form/form/form';
import { GiftCardCheckerFormActionType as ActionType } from '../types/action-type';
import { initialGiftCardCheckerFormState } from '../types/state';
import { giftCardCheckerFormActionReducer } from '../utils/action-reducer';
import { getNotificationDetailsForError } from '../utils/get-notification-details-for-error';
import { GiftCardCheckerField } from './field';
const b = bemClassBuilder('v-gift-card-checker-form');
const GiftCardCheckerFormBase = (props, ref) => {
    const { security } = useOccConfig();
    const { displayText, config, events } = props;
    const validationConfig = config.validation;
    const [state, dispatch] = useReducer(giftCardCheckerFormActionReducer, initialGiftCardCheckerFormState);
    const { validationResult, validator } = useFormValidator(validationConfig);
    const { formData, helpTextsById } = state;
    const handleSubmit = async () => {
        const { captcha } = formData, data = __rest(formData, ["captcha"]);
        await loadGiftCardDetails(data, captcha);
        dispatch({ type: ActionType.SuccessfullyLoadedGiftCard });
    };
    const captchaRef = useRef(null);
    const handleFailedSubmit = error => {
        var _a, _b;
        addErrorNotification(getNotificationDetailsForError(displayText, error), error);
        dispatch({ type: ActionType.FailedToLoadGiftCard });
        (_a = captchaRef.current) === null || _a === void 0 ? void 0 : _a.reset();
        (_b = events.onAfterFailedSubmit) === null || _b === void 0 ? void 0 : _b.call(events, error);
    };
    const handleFormSubmittingStateChanged = (isSubmitting) => {
        var _a;
        dispatch({ type: ActionType.SetFormSubmittingState, isSubmitting });
        (_a = events.onFormSubmittingStateChanged) === null || _a === void 0 ? void 0 : _a.call(events, isSubmitting);
    };
    const commonFieldProps = {
        validator,
        disabled: state.isSubmitting,
        config,
        displayText,
        validationResult,
        onChange: (fieldName, value) => {
            dispatch({ type: ActionType.SetFormData, formData: { [fieldName]: value } });
        },
        setHelpText: helpText => dispatch({ type: ActionType.SetHelpText, helpText }),
        validationConfig
    };
    return (_jsxs(Form, Object.assign({}, props, { className: b.block(), validator: () => validator.validateForm(formData), events: Object.assign(Object.assign({}, events), { onSubmit: handleSubmit, onAfterFailedSubmit: handleFailedSubmit, onFormSubmittingStateChanged: handleFormSubmittingStateChanged }), setFormData: getMergedData => {
            dispatch({ type: ActionType.SetFormData, formData: getMergedData(formData) });
        }, helpTexts: Object.values(helpTextsById), ref: ref }, { children: [_jsxs("div", Object.assign({ className: b.element('input-field-wrapper') }, { children: [_jsx(GiftCardCheckerField, Object.assign({}, commonFieldProps, { fieldName: "cardNumber", value: formData.cardNumber, error: validationResult.validationErrors.cardNumber })), validationConfig.pin.required && (_jsx(GiftCardCheckerField, Object.assign({}, commonFieldProps, { fieldName: "pin", value: formData.pin, error: validationResult.validationErrors.pin })))] })), security.captcha && (_jsx(GiftCardCheckerField, Object.assign({}, commonFieldProps, { captchaRef: captchaRef, fieldName: "captcha", value: formData.captcha, error: validationResult.validationErrors.captcha })))] })));
};
export const GiftCardCheckerForm = observer(forwardRef(GiftCardCheckerFormBase));
GiftCardCheckerForm.displayName = 'GiftCardCheckerForm';
